/**
 * Browser client for staff face enrolment and verification.
 *
 * Talks to `admin-api`, which holds the internal service secret and relays to
 * `svc-biometric-embedding`. Two things the browser deliberately does not do: see that secret,
 * and decide its own match verdict — a client that graded its own identity check could simply
 * always pass.
 *
 * **No subject is sent.** The endpoints derive the operator from the authenticated session, so
 * there is no field in which to name somebody else. The earlier prototype took a `subject_id`
 * from the request body, which meant any caller could enrol their own face against a
 * colleague's account.
 *
 * Requests go through `adminConsoleClient`, so they inherit the console's bearer token, request
 * ids, and its 401 handling — rather than a second, parallel notion of what an authenticated
 * request looks like.
 */

import { adminConsoleClient } from '../../../api/adminConsoleClient';
import type { IdentityObservation } from '../core/index.js';

/** Path under the admin-api mount root. */
const IDENTITY_BASE = '/work-focus/identity';

export interface EnrolmentResult {
  enrolled: boolean;
  dimensions: number;
  sampleCount: number;
  /**
   * Lowest similarity between the enrolment frames. A low value means the frames disagreed with
   * each other, and the resulting template will mis-verify its own owner.
   */
  minPairwiseSimilarity: number | null;
  rejected: string[];
}

export interface VerifyResult {
  verdict: IdentityObservation['verdict'];
  similarity: number | null;
  detail: string;
  /**
   * The threshold the **server** applied, so the caller can aggregate several samples against the
   * same number the server used on each one.
   *
   * This is returned, and must be used, because the threshold previously existed in two places:
   * `WORK_FOCUS_MIN_MATCH_SIMILARITY` on the server, and `sessionGuard.minMatchSimilarity` in the
   * shipped browser policy. Raising the server value alone had no effect on whether anything
   * locked — the client re-derived its own verdict from the client default and discarded the
   * server's. Null when the server did not say, in which case the caller falls back to policy.
   */
  minMatchSimilarity: number | null;
}

/** Encode a JPEG blob as base64 so the request stays plain JSON. */
async function toBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  // Chunked to avoid blowing the argument limit on large frames.
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function errorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { error?: unknown } } }).response;
  const detail = response?.data?.error;
  return typeof detail === 'string' && detail.length > 0 ? detail : fallback;
}

export async function enrolFace(frames: readonly Blob[]): Promise<EnrolmentResult> {
  const images = await Promise.all(frames.map((frame) => toBase64(frame)));
  try {
    const { data } = await adminConsoleClient.post<Record<string, unknown>>(
      `${IDENTITY_BASE}/enrol`,
      { images },
    );
    return {
      enrolled: data.enrolled === true,
      dimensions: Number(data.dimensions ?? 0),
      sampleCount: Number(data.sample_count ?? 0),
      minPairwiseSimilarity:
        typeof data.min_pairwise_similarity === 'number' ? data.min_pairwise_similarity : null,
      rejected: Array.isArray(data.rejected) ? data.rejected.map(String) : [],
    };
  } catch (error) {
    throw new Error(errorMessage(error, 'Enrolment failed'));
  }
}

/**
 * Compare one frame against the enrolled template.
 *
 * `faceCount` is sent for the audit trail only — the server never acts on it. It records how many
 * faces were in shot, which is the difference between "this threshold is wrong" and "we measured
 * the nearest face and it happened to be the enrolled one".
 */
export interface VerifyContext {
  faceCount?: number;
  /** Head pose at capture, so a score can be correlated with the angle that produced it. */
  yaw?: number;
  pitch?: number;
  /** Frame brightness and sharpness, to separate lighting effects from identity. */
  brightness?: number;
  blur?: number;
  /**
   * What the session guard believed when it asked. Logged, never acted on.
   *
   * This is the field that distinguishes "the guard never accumulated a mismatch" from "it
   * accumulated one and failed to act" — two very different bugs that produce the same symptom.
   */
  guardMismatchSeconds?: number;
  guardLocked?: boolean;
}

export async function verifyFace(frame: Blob, context: VerifyContext = {}): Promise<VerifyResult> {
  const image = await toBase64(frame);
  try {
    const { data } = await adminConsoleClient.post<Record<string, unknown>>(
      `${IDENTITY_BASE}/verify`,
      {
        image,
        // Diagnostic only — the server logs these and never acts on them. Without them a score
        // of 0.16 and a score of 0.53 from the same person are indistinguishable, and the cause
        // of the spread (angle? light? sharpness?) cannot be established from the log alone.
        ...(context.faceCount === undefined ? {} : { face_count: context.faceCount }),
        ...(context.yaw === undefined ? {} : { yaw: Math.round(context.yaw * 10) / 10 }),
        ...(context.pitch === undefined ? {} : { pitch: Math.round(context.pitch * 10) / 10 }),
        ...(context.brightness === undefined
          ? {}
          : { brightness: Math.round(context.brightness) }),
        ...(context.blur === undefined ? {} : { blur: Math.round(context.blur) }),
        ...(context.guardMismatchSeconds === undefined
          ? {}
          : { guard_mismatch_seconds: Math.round(context.guardMismatchSeconds) }),
        ...(context.guardLocked === undefined ? {} : { guard_locked: context.guardLocked }),
      },
    );
    const verdict = data.verdict;
    return {
      verdict:
        verdict === 'match' || verdict === 'mismatch' || verdict === 'unverified'
          ? verdict
          : 'unverified',
      similarity: typeof data.similarity === 'number' ? data.similarity : null,
      detail: typeof data.detail === 'string' ? data.detail : '',
      minMatchSimilarity:
        typeof data.min_match_similarity === 'number' ? data.min_match_similarity : null,
    };
  } catch (error) {
    // A failed call tells us nothing about who is at the desk, so it must not become a
    // mismatch — that would lock people out whenever the network hiccuped.
    return {
      verdict: 'unverified',
      similarity: null,
      detail: errorMessage(error, 'verification request failed'),
      minMatchSimilarity: null,
    };
  }
}

export interface EnrolmentStatus {
  enrolled: boolean;
  minMatchSimilarity: number;
  /** False when the service could not be reached, so "not enrolled" must not be assumed. */
  known: boolean;
}

export async function enrolmentStatus(): Promise<EnrolmentStatus> {
  try {
    const { data } = await adminConsoleClient.get<Record<string, unknown>>(
      `${IDENTITY_BASE}/status`,
    );
    return {
      enrolled: data.enrolled === true,
      minMatchSimilarity:
        typeof data.min_match_similarity === 'number' ? data.min_match_similarity : 0.36,
      known: true,
    };
  } catch {
    // Distinguished from a real "not enrolled": an unreachable service previously looked
    // identical to a new operator, which would have thrown an enrolled employee back into
    // enrolment and replaced their template.
    return { enrolled: false, minMatchSimilarity: 0.36, known: false };
  }
}

/** Grab the current video frame as a JPEG for a verification call. */
export async function captureFrame(video: HTMLVideoElement): Promise<Blob | null> {
  if (video.videoWidth === 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(video, 0, 0);
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}
